import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import profileStyles from "../CustomerProfile.module.scss";
import { APIService } from "../../../shared/shared-with-mobile/api-client/api.service";
import {
  ShopifyCustomerSummary,
  ShopifyOrder,
} from "../../../shared/shared-with-mobile/api-client/services/admin-analytics.service";

// ============================================================
// Customer Profile → Billing tab
//
// Pulls imported Shopify data for this email — the customer record (if
// any) plus every order tied to the email (including guest checkouts).
// Read-only against the shopify_* tables; the actual sync from Shopify
// runs out-of-band (one-shot bulk importer + future daily cron).
//
// Emits an empty state when no Shopify activity exists, which is the
// common case for license-holder emails that differ from the email used
// at checkout (parents buying for a coach, school admins purchasing).
// ============================================================

interface Props {
  email: string;
}

function fmtMoney(n: number | null, currency: string | null): string {
  if (n == null) return "—";
  const symbol = currency === "USD" ? "$" : `${currency ?? ""} `;
  return `${symbol}${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  const d = DateTime.fromISO(s);
  return d.isValid ? d.toFormat("MMM d, yyyy") : "—";
}

function statusBadge(status: string | null): React.ReactNode {
  if (!status) return <span style={{ color: "#ababb7" }}>—</span>;
  const colors: Record<string, string> = {
    paid: "#4CAF50",
    pending: "#FFC107",
    refunded: "#F44336",
    voided: "#ababb7",
    partially_paid: "#FFC107",
    partially_refunded: "#FFC107",
  };
  const color = colors[status] ?? "#ababb7";
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        padding: "2px 8px",
        borderRadius: 3,
        fontSize: 12,
        fontWeight: 500,
        textTransform: "capitalize",
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function lineItemsSummary(o: ShopifyOrder): string {
  if (!o.lineItems.length) return "—";
  const parts = o.lineItems.map((li) => {
    const t = li.title ?? "Item";
    return li.quantity > 1 ? `${li.quantity}× ${t}` : t;
  });
  const joined = parts.join(", ");
  return joined.length > 80 ? joined.slice(0, 77) + "..." : joined;
}

const BillingTab: React.FC<Props> = ({ email }) => {
  const [summary, setSummary] = useState<ShopifyCustomerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    APIService.ADMIN_ANALYTICS.GET_CUSTOMER_SHOPIFY(email)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        console.error("Failed to load Shopify summary", e);
        if (!cancelled) setError("Could not load Shopify data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email]);

  if (loading) {
    return (
      <div className={profileStyles.tabContent}>
        <div className={profileStyles.card}>Loading Shopify activity...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={profileStyles.tabContent}>
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>Shopify</h3>
          <p style={{ color: "#F44336" }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!summary || (!summary.customer && summary.orders.length === 0)) {
    return (
      <div className={profileStyles.tabContent}>
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>Shopify</h3>
          <p style={{ color: "#ababb7", marginTop: 8 }}>
            No Shopify activity found for <strong>{email}</strong>. The
            license-holder email may differ from the email this person uses at
            checkout (parents purchasing for a coach, school admins paying with
            finance-team accounts, etc.).
          </p>
        </div>
      </div>
    );
  }

  const { customer, orders, stats } = summary;

  return (
    <div className={profileStyles.tabContent}>
      <div className={profileStyles.cardGrid}>
        {/* Lifetime Stats Card */}
        <div className={profileStyles.card}>
          <h3 className={profileStyles.card__title}>Lifetime Spend</h3>
          <div className={profileStyles.statGrid}>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Total Spent</div>
              <div
                className={profileStyles.stat__value}
                style={{ color: "#4CAF50" }}
              >
                {fmtMoney(stats.totalSpent, stats.currency)}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Paid Orders</div>
              <div className={profileStyles.stat__value}>
                {stats.paidOrders}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Total Orders</div>
              <div className={profileStyles.stat__value}>
                {stats.totalOrders}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>First Purchase</div>
              <div className={profileStyles.stat__value}>
                {fmtDate(stats.firstOrderDate)}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Last Purchase</div>
              <div className={profileStyles.stat__value}>
                {fmtDate(stats.lastOrderDate)}
              </div>
            </div>
            <div className={profileStyles.stat}>
              <div className={profileStyles.stat__label}>Marketing Consent</div>
              <div className={profileStyles.stat__value}>
                {customer?.acceptsMarketing ? "Yes" : "No"}
              </div>
            </div>
          </div>
        </div>

        {/* Shopify Customer Card */}
        {customer && (
          <div className={profileStyles.card}>
            <h3 className={profileStyles.card__title}>Shopify Customer</h3>
            <div className={profileStyles.statGrid}>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Name</div>
                <div className={profileStyles.stat__value}>
                  {[customer.firstName, customer.lastName]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Phone</div>
                <div className={profileStyles.stat__value}>
                  {customer.phone || "—"}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Customer Since</div>
                <div className={profileStyles.stat__value}>
                  {fmtDate(customer.shopifyCreatedAt)}
                </div>
              </div>
              <div className={profileStyles.stat}>
                <div className={profileStyles.stat__label}>Tags</div>
                <div className={profileStyles.stat__value}>
                  {customer.tags || "—"}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order History */}
      <div className={profileStyles.card}>
        <h3 className={profileStyles.card__title}>
          Order History ({orders.length})
        </h3>
        {orders.length === 0 ? (
          <p style={{ color: "#ababb7" }}>No orders.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #424258" }}>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Date
                  </th>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Order #
                  </th>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Status
                  </th>
                  <th style={{ textAlign: "right", padding: 8, fontSize: 12 }}>
                    Total
                  </th>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>
                    Items
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr
                    key={o.shopifyId}
                    style={{ borderBottom: "1px solid #2a2c3a" }}
                  >
                    <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                      {fmtDate(o.shopifyCreatedAt)}
                    </td>
                    <td style={{ padding: 8, fontSize: 13 }}>
                      {o.orderNumber ? `#${o.orderNumber}` : "—"}
                    </td>
                    <td style={{ padding: 8 }}>
                      {statusBadge(o.financialStatus)}
                    </td>
                    <td
                      style={{
                        padding: 8,
                        fontSize: 13,
                        textAlign: "right",
                        fontWeight: 500,
                      }}
                    >
                      {fmtMoney(o.totalPrice, o.currency)}
                    </td>
                    <td style={{ padding: 8, fontSize: 13, color: "#ababb7" }}>
                      {lineItemsSummary(o)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingTab;
